import type { MRT_ColumnDef } from '@vrplatform/table';
import { formatCurrency } from '@vrplatform/utils';

export type FinancialAmountColumnType = 'sum' | 'subtract';

type FinancialAmountRow = {
  financials?: Record<string, number> | null | undefined;
  currency: string | undefined;
};

type FinancialAmountKey<TRow extends FinancialAmountRow> = keyof NonNullable<
  TRow['financials']
> &
  string;

const getFinancialAmountTotal = <TFinancialKey extends string>({
  amountKey,
  financials,
  type = 'sum',
  showNegative = false,
}: {
  amountKey: TFinancialKey[];
  financials: Partial<Record<TFinancialKey, number>> | null | undefined;
  type?: FinancialAmountColumnType;
  showNegative: boolean;
}): number => {
  if (type === 'sum') {
    const amount = amountKey.reduce(
      (acc, key) => acc + (financials?.[key] ?? 0),
      0
    );
    return showNegative ? -amount : amount;
  }

  const [firstKey, ...remainingKeys] = amountKey;
  const firstValue = firstKey ? (financials?.[firstKey] ?? 0) : 0;

  const amount = remainingKeys.reduce(
    (acc, key) => acc - (financials?.[key] ?? 0),
    firstValue
  );
  return showNegative ? -amount : amount;
};

export const getFinancialAmountColumn = <TRow extends FinancialAmountRow>({
  id,
  currency,
  header,
  amountKey,
  totals,
  type = 'sum',
  showNegative = false,
}: {
  id?: string;
  header: string;
  totals: Partial<Record<FinancialAmountKey<TRow>, number>> | undefined;
  currency: string | undefined;
  amountKey: FinancialAmountKey<TRow>[];
  type?: FinancialAmountColumnType;
  showNegative?: boolean;
}): MRT_ColumnDef<TRow> => {
  const footerTotal = getFinancialAmountTotal({
    amountKey,
    financials: totals,
    type,
    showNegative,
  });

  return {
    ...(id ? { id } : {}),
    header,
    minSize: 150,
    size: 150,
    mantineTableFooterCellProps: {
      align: 'right',
      sx: (theme) => ({
        justifyContent: 'flex-end',
        textAlign: 'right',
        paddingRight: theme.spacing.xs,
      }),
    },
    mantineTableBodyCellProps: { align: 'right' },
    mantineTableHeadCellProps: { align: 'right' },
    Cell: ({ row }) => {
      const total = getFinancialAmountTotal({
        amountKey,
        financials: row.original.financials as
          | Partial<Record<FinancialAmountKey<TRow>, number>>
          | null
          | undefined,
        type,
        showNegative,
      });

      return formatCurrency(total / 100, row.original.currency);
    },
    footer: formatCurrency(footerTotal / 100, currency),
  };
};
